
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { makeAIApiCall } from "@/components/dashboard/candidates/ai-api-service";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

const CONTENT_TYPES = ["Blog Post", "Social Media Post", "Ad Copy", "Product Description", "Email Newsletter", "Press Release"];

export const ContentCreationTab = () => {
  const [topic, setTopic] = useState("");
  const [description, setDescription] = useState("");
  const [contentType, setContentType] = useState(CONTENT_TYPES[0]);
  const [generatedContent, setGeneratedContent] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiEngine, setAiEngine] = useState('openai');
  const [tone, setTone] = useState("professional");
  const [user, setUser] = useState<any>(null);
  const [contentId, setContentId] = useState<string | null>(null);

  // Fetch user data on component mount
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        fetchLatestContent(user.id);
      }
    };
    getUser();
  }, []);

  // Fetch the most recent content for this user
  const fetchLatestContent = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('content_creation')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error) {
        console.error("Error fetching content:", error);
        return;
      }
      
      if (data && data.length > 0) {
        const latestContent = data[0];
        setContentId(latestContent.id);
        setContentType(latestContent.content_type);
        setTopic(latestContent.topic);
        setTone(latestContent.tone);
        setAiEngine(latestContent.ai_engine);
        setDescription(latestContent.description);
        setGeneratedContent(latestContent.generated_contents || "");
      }
    } catch (error) {
      console.error("Error in fetchLatestContent:", error);
    }
  };

  // Save content to Supabase
  const saveContentToSupabase = async (content: string) => {
    if (!user) return;
    
    try {
      const contentData = {
        user_id: user.id,
        content_type: contentType,
        topic,
        tone,
        ai_engine: aiEngine,
        description,
        generated_contents: content
      };
      
      let response;
      if (contentId) {
        // Update existing content
        response = await supabase
          .from('content_creation')
          .update({
            ...contentData,
            updated_at: new Date().toISOString()
          })
          .eq('id', contentId);
      } else {
        // Insert new content
        response = await supabase
          .from('content_creation')
          .insert(contentData);
          
        // Fetch the ID of the newly created content
        if (!response.error) {
          const { data } = await supabase
            .from('content_creation')
            .select('id')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1);
            
          if (data && data.length > 0) {
            setContentId(data[0].id);
          }
        }
      }
      
      if (response.error) {
        console.error("Error saving content:", response.error);
        toast({
          title: "Error",
          description: "Failed to save content to database",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error in saveContentToSupabase:", error);
    }
  };

  const generateContent = async () => {
    if (!topic || !description) return;
    setIsGenerating(true);
    setGeneratedContent("");
    try {
      const messages = [{
        role: "system",
        content: "You are an expert copywriter and content creator. Create high-quality, engaging, and SEO-optimized content based on the user's requirements."
      }, {
        role: "user",
        content: `Create a ${contentType.toLowerCase()} about "${topic}". 
          
          Description/Requirements: ${description}
          
          Tone: ${tone}
          
          Please make this content engaging, informative, and optimized for both readers and search engines. Include an attention-grabbing headline or title.`
      }];
      
      const response = await makeAIApiCall("/v1/chat/completions", messages, aiEngine === "openai" ? "gpt-5.4-nano" : "deepseek-chat", aiEngine);
      
      if (response.ok) {
        const data = await response.json();
        let content = "";
        
        if (aiEngine === "openai") {
          content = data.choices[0].message.content;
        } else {
          content = data.choices[0].message.content;
        }
        
        setGeneratedContent(content);
        
        // Save to Supabase
        await saveContentToSupabase(content);
      } else {
        console.error("API request failed");
        setGeneratedContent("Failed to generate content. Please try again.");
      }
    } catch (error) {
      console.error("Error generating content:", error);
      setGeneratedContent("An error occurred while generating content. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return <div className="space-y-6 text-white">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-secondary/50 border-2 border-primary h-[540px]">
          <CardHeader>
            <CardTitle className="text-white">Content Generator</CardTitle>
            <CardDescription className="text-gray-300">
              Create professional content with AI assistance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-primary-light w-1/3">Content Type</label>
              <div className="w-2/3">
                <Select value={contentType} onValueChange={setContentType}>
                  <SelectTrigger className="bg-[#1A1F2C] border-white text-white w-full">
                    <SelectValue placeholder="Select content type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTENT_TYPES.map(type => <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-primary-light w-1/3">Topic/Title</label>
              <Input placeholder="E.g., 'Benefits of Remote Work'" value={topic} onChange={e => setTopic(e.target.value)} className="bg-[#1A1F2C] border-white text-white w-2/3" />
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-primary-light w-1/3">Tone</label>
              <div className="w-2/3">
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="bg-[#1A1F2C] border-white text-white w-full">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="authoritative">Authoritative</SelectItem>
                    <SelectItem value="enthusiastic">Enthusiastic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-primary-light w-1/3">AI Engine</label>
              <div className="w-2/3">
                <Select value={aiEngine} onValueChange={setAiEngine}>
                  <SelectTrigger className="bg-[#1A1F2C] border-primary/20 text-white border-white w-full">
                    <SelectValue placeholder="Select AI Engine" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="openai">OpenAI</SelectItem>
                    <SelectItem value="deepseek">DeepSeek</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-primary-light">Description/Requirements</label>
              <Textarea placeholder="Describe what you want the content to cover..." value={description} onChange={e => setDescription(e.target.value)} className="bg-[#1A1F2C] border-white text-white min-h-[100px]" />
            </div>

            
            <div className="flex justify-center">
              <Button onClick={generateContent} disabled={isGenerating || !topic || !description} className="mt-4 bg-primary text-black hover:bg-primary/80 w-auto">
                {isGenerating ? <span className="flex items-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </span> : "Generate Content"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2 bg-secondary/50 border-2 border-primary h-[540px]">
          <CardHeader>
            <CardTitle className="text-white">Generated Content</CardTitle>
            <CardDescription className="text-gray-300 border-white">
              Your AI-generated content will appear here
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isGenerating ? <div className="flex flex-col items-center justify-center min-h-[300px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-gray-300">Creating your {contentType.toLowerCase()}...</p>
              </div> : generatedContent ? <div className="bg-[#1A1F2C] p-6 rounded-md border text-[12px] border-white h-[410px] whitespace-pre-line overflow-auto max-h-[500px]">
                {generatedContent}
              </div> : <div className="flex flex-col items-center justify-center text-[12px] text-gray-300 h-[410px] text-[13px]">
                <p>Fill in the form and click "Generate Content" to create your {contentType.toLowerCase()}</p>
              </div>}
          </CardContent>
        </Card>
      </div>

      {generatedContent && <Card className="bg-secondary/50 border-none">
          <CardContent>
            <div className="flex flex-wrap gap-2 items-center justify-center">
              <Button onClick={() => navigator.clipboard.writeText(generatedContent)} className="bg-primary text-black hover:bg-primary/80">
                Copy to Clipboard
              </Button>
              <Button 
                onClick={() => {
                  setGeneratedContent("");
                  // Also update in database 
                  if (contentId && user) {
                    supabase
                      .from('content_creation')
                      .update({ 
                        generated_contents: "",
                        updated_at: new Date().toISOString()
                      })
                      .eq('id', contentId);
                  }
                }}
                variant="outline"
                className="border-primary/20 text-black"
              >
                Clear Content
              </Button>
              <Button 
                onClick={() => {
                  const blob = new Blob([generatedContent], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${contentType.replace(/\s+/g, '-').toLowerCase()}-${topic.substring(0, 20).replace(/\s+/g, '-').toLowerCase()}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                variant="outline"
                className="border-primary/20 text-black"
              >
                Download as Text
              </Button>
            </div>
          </CardContent>
        </Card>}
    </div>;
};
